// src/PricingPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePlanTracker, PLANS } from './hooks/usePlanTracker';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PricingPage.css';

const STRIPE_PAYMENT_LINKS = {
  PRO:       'https://buy.stripe.com/test_XXXXXXXXXXXXX',
  UNLIMITED: 'https://buy.stripe.com/test_YYYYYYYYYYYYY',
};

const pricingPlans = [
  { ...PLANS.FREE,      key: 'FREE',      paymentLink: null },
  { ...PLANS.PRO,       key: 'PRO',       paymentLink: STRIPE_PAYMENT_LINKS.PRO },
  { ...PLANS.UNLIMITED, key: 'UNLIMITED',  paymentLink: STRIPE_PAYMENT_LINKS.UNLIMITED },
];

// ── tiny icon helpers ─────────────────────────
function CheckIcon({ color = 'var(--green)' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`pp-faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="pp-faq-question">
        <span>{question}</span>
        <svg className="pp-faq-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="pp-faq-answer"><p>{answer}</p></div>
    </div>
  );
}

const FAQS = [
  { question: 'Do I need an account for the free plan?', answer: 'No — you can generate up to 2 notices per month without signing in. An account is required for Pro and Unlimited plans.' },
  { question: 'Can I upgrade or downgrade at any time?', answer: 'Yes. You can upgrade instantly. To downgrade, cancel your current subscription via the Stripe Customer Portal and your plan reverts at the end of the billing period.' },
  { question: 'What happens when I hit my monthly limit?', answer: 'You\'ll be shown an upgrade prompt. Your existing notices remain accessible — you just can\'t generate new ones until the next billing cycle or you upgrade.' },
  { question: 'Are the generated notices real legal documents?', answer: 'They are structured to comply with 17 U.S.C. § 512(c)(3) requirements. However, this tool is not a substitute for legal advice. For complex cases, consult an attorney.' },
  { question: 'How does the signature work?', answer: 'You can draw your signature in-app or upload an image. It gets embedded directly into the PDF at the position and size you choose.' },
  { question: 'Can I cancel my subscription?', answer: 'Yes, cancel any time via the Stripe Customer Portal. You keep access to paid features until the end of the billing period.' },
];

export default function PricingPage() {
  const { user, authLoading } = useAuth();
  const navigate              = useNavigate();
  const planTracker           = usePlanTracker(user?.uid);
  const [searchParams]        = useSearchParams();
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);

  const currentPlan  = planTracker?.currentPlan || 'FREE';
  const planLoading  = planTracker?.loading ?? true;
  const usageStats   = user && planTracker?.getUsageStats ? planTracker.getUsageStats() : null;

  // Success redirect from Stripe
  useEffect(() => {
    const ok   = searchParams.get('success');
    const plan = searchParams.get('plan');
    if (ok === 'true' && plan) {
      setSuccess(`Payment successful! Your ${plan.toUpperCase()} plan will be active within 5 minutes.`);
      setTimeout(() => window.history.replaceState({}, '', window.location.pathname), 3000);
    }
  }, [searchParams]);

  const handleSelect = (plan) => {
    setError(null); setSuccess(null);

    if (plan.key === 'FREE') {
      if (currentPlan === 'FREE') { setError("You're already on the Starter plan."); return; }
      setError('To downgrade, cancel your subscription via the Stripe Customer Portal.');
      return;
    }
    if (!user) { navigate('/login'); return; }
    if (currentPlan === plan.key) { setError(`You're already on the ${plan.name} plan.`); return; }

    if (plan.paymentLink) {
      localStorage.setItem('pendingPayment', JSON.stringify({
        userId: user.uid, userEmail: user.email,
        plan: plan.key.toLowerCase(), timestamp: new Date().toISOString(),
      }));
      window.location.href = plan.paymentLink;
    } else {
      setError('Payment link not configured.');
    }
  };

  if (authLoading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <span>Loading plans…</span>
      </div>
    );
  }

  return (
    <div className="pp-root">
      {/* header */}
      <div className="pp-header">
        
        <div className="pp-eyebrow">Pricing</div>
        <h1 className="pp-title">Simple, honest <em>pricing.</em></h1>
        <p className="pp-sub">No hidden fees. No card for Starter. Cancel any time.</p>

        {/* current plan banner */}
        {user && usageStats && !planLoading && (
          <div className="pp-current-banner">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            Current plan: <strong>{planTracker.planConfig.name}</strong>
            <span className="pp-banner-dot">·</span>
            {usageStats.qrRemaining === 'Unlimited'
              ? 'Unlimited notices'
              : `${usageStats.qrRemaining} notices remaining this month`}
          </div>
        )}

        {success && (
          <div className="pp-alert pp-alert-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
            {success}
          </div>
        )}
        {error && (
          <div className="pp-alert pp-alert-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            {error}
          </div>
        )}
      </div>

      {/* plan cards */}
      <div className="pp-grid">
        {pricingPlans.map((plan) => {
          const isCurrent = user && currentPlan === plan.key;
          const isFeatured = plan.popular;
          return (
            <div key={plan.key} className={`pp-card ${isFeatured ? 'pp-card-featured' : ''} ${isCurrent ? 'pp-card-current' : ''}`}>
              {/* badge */}
              <span className={`pp-badge ${
                plan.key === 'FREE'      ? 'pp-badge-free'  :
                plan.key === 'PRO'       ? 'pp-badge-pop'   : 'pp-badge-power'
              }`}>
                {plan.key === 'FREE' ? 'Free' : plan.key === 'PRO' ? 'Most popular' : 'Power user'}
              </span>

              <div className="pp-plan-name">{plan.name}</div>
              <div className="pp-price">
                ${plan.price}
                <span className="pp-period">/ {plan.period === 'Forever' ? 'month' : 'month'}</span>
              </div>
              {plan.key === 'FREE' && <div className="pp-free-note">Free forever</div>}
              <p className="pp-plan-desc">{plan.description}</p>

              {/* notices pill */}
              <div className="pp-pill">
                <span className="pp-pill-label">Notices / month</span>
                <span className="pp-pill-val">
                  {plan.limits.isUnlimited ? 'Unlimited' : plan.limits.qrPerMonth}
                </span>
              </div>

              <div className="pp-divider" />

              {/* features */}
              <ul className="pp-feats">
                {plan.features.map((f, i) => (
                  <li key={i} className="pp-feat">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                className={`pp-btn ${isFeatured ? 'pp-btn-primary' : 'pp-btn-outline'} ${isCurrent ? 'pp-btn-current' : ''}`}
                onClick={() => handleSelect(plan)}
                disabled={isCurrent}
              >
                {isCurrent ? (
                  <><CheckIcon color="var(--green)" /> Current plan</>
                ) : !user && plan.key !== 'FREE' ? (
                  'Sign in to subscribe'
                ) : plan.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* notes row */}
      <div className="pp-notes">
        <span>✓ No card for Starter</span>
        <span>✓ Cancel any time</span>
        <span>✓ Month-to-month</span>
      </div>

      {/* usage stats */}
      {user && usageStats && !planLoading && (
        <div className="pp-usage-section">
          <h3 className="pp-usage-title">Your usage this month</h3>
          <div className="pp-usage-grid">
            {[
              ['Notices generated', usageStats.qrGenerated],
              ['Notices remaining', usageStats.qrRemaining],
              ['Monthly limit',     usageStats.qrLimit],
              ['Resets on', usageStats.resetDate
                ? new Date(usageStats.resetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'],
            ].map(([label, val]) => (
              <div key={label} className="pp-usage-card">
                <div className="pp-usage-label">{label}</div>
                <div className="pp-usage-val">{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="pp-faq-section">
        <h3 className="pp-faq-title">Common questions</h3>
        <div className="pp-faq-list">
          {FAQS.map((f, i) => <FAQItem key={i} question={f.question} answer={f.answer} />)}
        </div>
      </div>

      <p className="pp-legal">Not legal advice. For complex copyright matters, consult a qualified attorney.</p>
    </div>
  );
}